import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { useSession } from '../auth/session.tsx';
import { queryDefaults } from './queries.ts';

/**
 * Marshalling a client's accounts, bank by bank (166, P-33; api/src/routes/marshalling.ts). Every rule is the
 * database's: when a bank is marshalled, when "Marshal client accounts" completes, the three tasks, who records.
 * Amounts arrive as the database's text and are shown as they are, never through a float. Accounts are known by their
 * last four only.
 */

export type StepKind = 'appointment_set' | 'documents_scanned' | 'bank_review' | 'return_visit' | 'check_received'
  | 'check_deposited' | 'statements_received' | 'internal_move' | 'no_record' | 'all_closed_before';
export type AccountKind = 'checking' | 'savings' | 'certificate_of_deposit' | 'money_market' | 'brokerage' | 'retirement'
  | 'cash_on_hand' | 'rep_payee' | 'other';
export type AccountOutcome = 'Closed' | 'Retitled' | 'Not the client\'s' | 'Already closed when found';

export interface MarshalInstitution {
  id: string; name: string; withdrawn: boolean; withdrawal_reason: string | null;
  dropped: boolean; marshalled_on: string | null; marshalled_how: 'last check' | 'statements' | 'internal move' | null;
  partial: boolean; versions: number;
}
export interface MarshalAccount {
  id: string; marshal_institution_id: string | null; institution_name: string; kind: AccountKind; last4: string;
  title_as_found: string | null; found_on: string | null; found_how: string | null;
  role: 'held at marshalling' | 'guardianship'; marshal_outcome: AccountOutcome | null; marshal_outcome_on: string | null;
  withdrawn: boolean; withdrawal_reason: string | null; fields: Record<string, unknown>;
}
export interface MarshalStep {
  id: string; marshal_institution_id: string; kind: StepKind; on_date: string;
  review_outcome: 'approved' | 'more documents needed' | 'refused' | null; letter_brought: boolean | null;
  certified_copies: number | null; other_text: string | null; amount: string | null; is_last: boolean | null;
  is_partial: boolean | null; approx_remaining: string | null; court_order_id: string | null; under_order_id: string | null;
  account_id: string | null; check_action_id: string | null; has_document: boolean;
  withdrawn: boolean; withdrawal_reason: string | null; fields: Record<string, unknown>; deposited: boolean;
}
export interface MarshalOrder {
  id: string; signed_date: string | null; order_type: string | null; authorizes_further_marshalling: boolean;
  further_institution_ids: string[];
}
export interface MarshalDeadline {
  id: string; status: string; due_date: string; completed_on: string | null; met_by_marshalling: boolean;
}
export interface MarshalTask {
  id: string; title: string; status: string; department: string;
  made_by_rule: 'marshal_statements' | 'marshal_bank_refused' | 'marshal_further';
  marshal_institution_id: string | null; marshal_action_id: string | null; further_order_id: string | null;
}
export interface ClientMarshalling {
  institutions: MarshalInstitution[]; accounts: MarshalAccount[]; actions: MarshalStep[]; orders: MarshalOrder[];
  deadlines: MarshalDeadline[]; no_accounts: { id: string; on_date: string } | null; tasks: MarshalTask[];
  may_record: boolean; may_set_order: boolean;
}
export interface FurtherMarshalling {
  orders: MarshalOrder[]; institutions: { id: string; name: string; partial: boolean; marshalled_on: string | null }[];
  may_set_order: boolean;
}
export interface CheckNotDeposited {
  client_id: string; client_ref: string | null; marshal_institution_id: string; institution: string;
  check_action_id: string; received_on: string; listed_from: string;
}

export function useClientMarshalling(clientId: string, enabled = true) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'marshalling'],
    queryFn: () => apiFetch<ClientMarshalling>(`/clients/${clientId}/marshalling`, getToken),
    ...queryDefaults,
    enabled,
  });
}

export function useFurtherMarshalling(clientId: string) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'further-marshalling'],
    queryFn: () => apiFetch<FurtherMarshalling>(`/clients/${clientId}/further-marshalling`, getToken),
    ...queryDefaults,
  });
}

export function useChecksNotDeposited(enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['marshalling', 'checks-not-deposited'],
    queryFn: () => apiFetch<{ checks: CheckNotDeposited[] }>('/marshalling/checks-not-deposited', getToken),
    ...queryDefaults,
    enabled,
  });
}

function post<T>(getToken: () => Promise<string>, path: string, body: unknown): Promise<T> {
  return apiFetch<T>(path, getToken, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
  });
}

/** Everything a step can change: the client's marshalling and deadline, the tasks the outcomes open, the triage list. */
function useMarshallingChanged(clientId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['client', clientId] });
    void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    void queryClient.invalidateQueries({ queryKey: ['marshalling'] });
  };
}

function useMarshalPost<V>(clientId: string, path: (v: V) => string, body: (v: V) => unknown) {
  const { getToken } = useSession();
  const changed = useMarshallingChanged(clientId);
  return useMutation({ mutationFn: (v: V) => post<unknown>(getToken, path(v), body(v)), onSuccess: changed });
}

export const useAddInstitution = (clientId: string) =>
  useMarshalPost<{ name: string }>(clientId, () => `/clients/${clientId}/marshal-institutions`, (v) => v);
export const useAddAccount = (clientId: string) =>
  useMarshalPost<{ institution_id?: string | null; institution_name?: string | null; kind: AccountKind; last4: string;
    title_as_found?: string | null; found_on?: string | null; found_how?: string | null;
    role: 'held at marshalling' | 'guardianship' }>(clientId, () => `/clients/${clientId}/guardianship-accounts`, (v) => v);
export const useRecordStep = (clientId: string) =>
  useMarshalPost<{ institutionId: string; kind: StepKind; fields: Record<string, unknown>;
    accounts?: { account_id: string; outcome: AccountOutcome }[] }>(clientId,
    (v) => `/marshal-institutions/${v.institutionId}/steps`,
    ({ institutionId: _i, ...b }) => b);
export const useCorrectStep = (clientId: string) =>
  useMarshalPost<{ stepId: string; fields: Record<string, unknown>; reason: string; reason_text?: string }>(clientId,
    (v) => `/marshal-steps/${v.stepId}/correct`, ({ stepId: _s, ...b }) => b);
export const useCorrectAccount = (clientId: string) =>
  useMarshalPost<{ accountId: string; fields: Record<string, unknown>; reason: string; reason_text?: string }>(clientId,
    (v) => `/guardianship-accounts/${v.accountId}/correct`, ({ accountId: _a, ...b }) => b);
export const useWithdrawStep = (clientId: string) =>
  useMarshalPost<{ stepId: string; reason: string }>(clientId, (v) => `/marshal-steps/${v.stepId}/withdraw`,
    ({ stepId: _s, ...b }) => b);
export const useWithdrawInstitution = (clientId: string) =>
  useMarshalPost<{ institutionId: string; reason: string }>(clientId,
    (v) => `/marshal-institutions/${v.institutionId}/withdraw`, ({ institutionId: _i, ...b }) => b);
export const useNoAccountsFound = (clientId: string) =>
  useMarshalPost<{ on_date: string }>(clientId, () => `/clients/${clientId}/no-accounts-found`, (v) => v);
export const useSetFurtherMarshalling = (clientId: string) =>
  useMarshalPost<{ orderId: string; authorizes: boolean; institution_ids: string[]; reason: string; reason_text?: string }>(
    clientId, (v) => `/court_orders/${v.orderId}/further-marshalling`, ({ orderId: _o, ...b }) => b);

